import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, TrendingUp, TrendingDown } from "lucide-react";

// 2025 tax year — simplified US federal estimator.
// Numbers from IRS Rev. Proc. 2024-40 (2025 inflation adjustments).
// For a full return, clients should submit the intake form.

const STD_DEDUCTION_2025 = {
  single: 15000, mfj: 30000, mfs: 15000, hoh: 22500, qw: 30000,
};

// Piecewise brackets (TY 2025). Each entry: [top of bracket, marginal rate].
const BRACKETS_2025 = {
  single: [[11925, 0.10], [48475, 0.12], [103350, 0.22], [197300, 0.24], [250525, 0.32], [626350, 0.35], [Infinity, 0.37]],
  mfj:    [[23850, 0.10], [96950, 0.12], [206700, 0.22], [394600, 0.24], [501050, 0.32], [751600, 0.35], [Infinity, 0.37]],
  mfs:    [[11925, 0.10], [48475, 0.12], [103350, 0.22], [197300, 0.24], [250525, 0.32], [375800, 0.35], [Infinity, 0.37]],
  hoh:    [[17000, 0.10], [64850, 0.12], [103350, 0.22], [197300, 0.24], [250500, 0.32], [626350, 0.35], [Infinity, 0.37]],
  qw:     [[23850, 0.10], [96950, 0.12], [206700, 0.22], [394600, 0.24], [501050, 0.32], [751600, 0.35], [Infinity, 0.37]],
};

// EITC 2025 — rough parameters. plateauStart is where the phase-in maxes out.
// phaseoutStart differs for joint vs non-joint; we pick based on filing status.
const EITC_2025 = {
  0: { max: 649,  phaseIn: 0.0765, plateauStart: 8490,  phaseoutStart: [10620, 17730], phaseoutRate: 0.0765 },
  1: { max: 4328, phaseIn: 0.34,   plateauStart: 12730, phaseoutStart: [23350, 30470], phaseoutRate: 0.1598 },
  2: { max: 7152, phaseIn: 0.40,   plateauStart: 17880, phaseoutStart: [23350, 30470], phaseoutRate: 0.2106 },
  3: { max: 8046, phaseIn: 0.45,   plateauStart: 17880, phaseoutStart: [23350, 30470], phaseoutRate: 0.2106 },
};
// EITC AGI cap 2025 (rough max where EITC hits zero) — sanity guard.
const EITC_MAX_AGI = { 0: [19104, 26214], 1: [50434, 57554], 2: [56004, 63398], 3: [61555, 68675] };

function computeTax(taxable, filing) {
  if (taxable <= 0) return 0;
  const brackets = BRACKETS_2025[filing] || BRACKETS_2025.single;
  let tax = 0, lower = 0;
  for (const [top, rate] of brackets) {
    if (taxable > top) {
      tax += (top - lower) * rate;
      lower = top;
    } else {
      tax += (taxable - lower) * rate;
      return tax;
    }
  }
  return tax;
}

// Additional CTC (refundable portion of the Child Tax Credit).
// Non-refundable CTC (up to $2000/kid) offsets tax first; whatever's left up to $1,700/kid
// is refundable — but only up to 15% × (earned − $2,500). Ignores high-AGI phaseout for MVP.
function splitCTC(kids, tax, earnedIncome) {
  const kidsN = Math.max(0, kids);
  const totalCTC = 2000 * kidsN;
  const nonRefundable = Math.min(totalCTC, Math.max(0, tax));
  const leftover = totalCTC - nonRefundable;
  const earnedIncomeLimit = 0.15 * Math.max(0, earnedIncome - 2500);
  const perKidRefundCap = 1700 * kidsN;
  const additionalCTC = Math.min(leftover, perKidRefundCap, earnedIncomeLimit);
  return { nonRefundable, additionalCTC };
}

// EITC (refundable). MFS not eligible on Federal for TY2025 unless separated - keep simple: MFS = 0.
function computeEITC(kids, filing, earnedIncome, agi) {
  if (filing === "mfs") return 0;
  const k = Math.min(Math.max(0, kids), 3);
  const p = EITC_2025[k];
  const jointIdx = (filing === "mfj" || filing === "qw") ? 1 : 0;
  const income = Math.max(earnedIncome, agi); // EITC uses the greater for phaseout
  let eitc;
  if (earnedIncome <= 0) return 0;
  if (earnedIncome < p.plateauStart) {
    eitc = earnedIncome * p.phaseIn;
  } else {
    eitc = p.max;
  }
  const phaseoutStart = p.phaseoutStart[jointIdx];
  if (income > phaseoutStart) {
    eitc = Math.max(0, eitc - (income - phaseoutStart) * p.phaseoutRate);
  }
  const cap = EITC_MAX_AGI[k][jointIdx];
  if (income >= cap) return 0;
  return Math.max(0, eitc);
}

const num = (v) => {
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const money = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function RefundCalculator({ compact = false, context = "default" }) {
  const [filing, setFiling] = useState("single");
  const [wages, setWages] = useState("");
  const [withheld, setWithheld] = useState("");
  const [otherIncome, setOtherIncome] = useState("");
  const [kids, setKids] = useState("");
  const [otherDeps, setOtherDeps] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // If any input changes after a calculation, clear the shown result so a stale
  // number can't linger — user has to press "Calculate" again for the new inputs.
  useEffect(() => { setSubmitted(false); }, [filing, wages, withheld, otherIncome, kids, otherDeps]);

  const result = useMemo(() => {
    const w = num(wages), wh = num(withheld), oi = num(otherIncome);
    const earnedIncome = w; // wages are the earned-income input for EITC/ACTC
    const agi = w + oi;
    const taxable = Math.max(0, agi - STD_DEDUCTION_2025[filing]);
    const tax = computeTax(taxable, filing);
    const { nonRefundable, additionalCTC } = splitCTC(num(kids), tax, earnedIncome);
    const odc = Math.max(0, num(otherDeps)) * 500; // non-refundable
    const nonRefundableTotal = Math.min(tax, nonRefundable + odc);
    const taxAfterCredits = Math.max(0, tax - nonRefundableTotal);
    const eitc = computeEITC(num(kids), filing, earnedIncome, agi);
    const refundOrOwed = wh - taxAfterCredits + additionalCTC + eitc;
    return {
      income: agi,
      taxable,
      tax,
      nonRefundableCredits: nonRefundableTotal,
      additionalCTC,
      eitc,
      taxAfterCredits,
      refundOrOwed,
    };
  }, [filing, wages, withheld, otherIncome, kids, otherDeps]);

  const isRefund = result.refundOrOwed >= 0;
  const hasEnoughInput = num(wages) > 0 || num(otherIncome) > 0;

  return (
    <div className="border border-border rounded-lg bg-card p-6 lg:p-8" data-testid="refund-calculator">
      <div className="flex items-center gap-2 mb-2 text-primary">
        <Calculator size={16} />
        <span className="label-caps">Refund estimator · TY 2025</span>
      </div>
      {context === "amend_success" ? (
        <>
          <h3 className="font-serif text-3xl mb-2">Estimate the new refund.</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Rerun the numbers with the corrected figures to see roughly what the amended refund could be. Your preparer will confirm the real number.
          </p>
        </>
      ) : context === "intake_success" ? (
        <>
          <h3 className="font-serif text-3xl mb-2">Ballpark your refund.</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Play with the numbers while your preparer reviews. Includes standard deduction, CTC + Additional CTC, and EITC.
          </p>
        </>
      ) : (
        <>
          <h3 className="font-serif text-3xl mb-2">See what you could get back.</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Includes standard deduction, Child Tax Credit (with refundable ACTC), and Earned Income Tax Credit. Not tax advice — submit the intake form for the real number.
          </p>
        </>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); if (hasEnoughInput) setSubmitted(true); }}
        className="space-y-6"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Filing status</Label>
            <select
              data-testid="calc-filing-status"
              value={filing}
              onChange={(e) => setFiling(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="single">Single</option>
              <option value="mfj">Married filing jointly</option>
              <option value="mfs">Married filing separately</option>
              <option value="hoh">Head of household</option>
              <option value="qw">Qualifying surviving spouse</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">W-2 wages (this year)</Label>
            <Input inputMode="numeric" placeholder="$45,000" value={wages} onChange={(e) => setWages(e.target.value)} data-testid="calc-wages" />
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Federal tax withheld (W-2 box 2)</Label>
            <Input inputMode="numeric" placeholder="$3,500" value={withheld} onChange={(e) => setWithheld(e.target.value)} data-testid="calc-withheld" />
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Other taxable income</Label>
            <Input inputMode="numeric" placeholder="1099 / self-employment" value={otherIncome} onChange={(e) => setOtherIncome(e.target.value)} data-testid="calc-other-income" />
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]"># of children under 17</Label>
            <Input inputMode="numeric" placeholder="0" value={kids} onChange={(e) => setKids(e.target.value)} data-testid="calc-kids" />
          </div>
          <div className="space-y-1">
            <Label className="label-caps text-[10px]">Other dependents</Label>
            <Input inputMode="numeric" placeholder="0" value={otherDeps} onChange={(e) => setOtherDeps(e.target.value)} data-testid="calc-other-deps" />
          </div>
        </div>

        <Button
          type="submit"
          data-testid="calc-submit"
          disabled={!hasEnoughInput}
          className="w-full md:w-auto min-w-[240px]"
        >
          <Calculator size={16} className="mr-2" />
          {submitted ? "Recalculate refund" : "Calculate my refund"}
        </Button>
        {!hasEnoughInput && (
          <div className="text-xs text-muted-foreground -mt-4" data-testid="calc-input-hint">
            Enter your W-2 wages (or other income) to enable the calculator.
          </div>
        )}
      </form>

      {submitted && (
        <div
          className={`mt-6 rounded-md p-5 border ${isRefund ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`}
          data-testid="calc-result"
        >
          <div className="flex items-center gap-2 mb-1">
            {isRefund ? <TrendingUp size={16} className="text-primary" /> : <TrendingDown size={16} className="text-destructive" />}
            <span className="label-caps text-[10px]">{isRefund ? "Estimated refund" : "Estimated balance due"}</span>
          </div>
          <div className={`font-serif text-4xl leading-none mb-3 ${isRefund ? "text-primary" : "text-destructive"}`} data-testid="calc-refund-amount">
            {money(Math.abs(result.refundOrOwed))}
          </div>
          {!compact && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-4 pt-4 border-t border-border">
              <Metric k="Total income" v={money(result.income)} />
              <Metric k="Taxable income" v={money(result.taxable)} />
              <Metric k="Tax before credits" v={money(Math.round(result.tax))} />
              <Metric k="Non-refundable credits" v={money(Math.round(result.nonRefundableCredits))} />
              <Metric k="Additional CTC" v={money(Math.round(result.additionalCTC))} testid="calc-actc" />
              <Metric k="EITC" v={money(Math.round(result.eitc))} testid="calc-eitc" />
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-4">
        Uses 2025 federal brackets, standard deduction, CTC + Additional CTC (up to $1,700/child refundable), and Earned Income Tax Credit (rough). Ignores state tax, AMT, investment-income limits, itemized deductions, and high-AGI phaseouts. Your real refund can differ — always confirm with a preparer.
      </p>
    </div>
  );
}

const Metric = ({ k, v, testid }) => (
  <div data-testid={testid}>
    <div className="label-caps text-[9px]">{k}</div>
    <div className="text-sm font-medium">{v}</div>
  </div>
);
